import { useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  Download,
  Headphones,
  PhoneMissed,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { fetchQueues } from '@/services/api';
import type { Queue, QueueCallDetail, QueueDetail } from '@/types';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { loadPdfAssets } from '@/lib/pdf/pdfAssets';
import { describeFilters } from '@/lib/reports/schedule';
import {
  filterQueueRows,
  formatDuration,
  formatFailover,
  formatTimeout,
  isMissOrAbandon,
  loadQueueMissed,
  normalizeStatus,
  outcomeHelp,
  outcomeLabel,
  queueConfigResolver,
  queueMissedCsv,
  queueMissedPdf,
  summarizeQueueRows,
} from '@/lib/reports/queueMissedReport';
import { downloadText } from './download';
import { ScheduleReportButton } from './ScheduleReportButton';
import { getPresetHtmlDateRange } from '@/utils/helpers';
import { ContactNameLine } from '@/components/crm/CallerName';

type DatePreset = 'today' | 'week' | 'month' | 'custom';

function defaultDateRange() {
  return getPresetHtmlDateRange('week');
}

/**
 * Compact Call From for ~10-digit display.
 * "0215553247 Avoxi<27215553247>" → primary + optional second line.
 */
function formatCallFrom(value?: string): { primary: string; secondary?: string } {
  if (!value || !value.trim()) return { primary: '—' };
  const raw = value.trim();

  // Capture content in angle brackets and loose digit runs
  const bracket = raw.match(/<([^>]+)>/);
  const withoutBrackets = raw.replace(/<[^>]*>/g, ' ').trim();
  const digitRuns = withoutBrackets.match(/\+?\d[\d\s-]{5,}\d/g)?.map((s) => s.replace(/\s+/g, '')) || [];
  const words = withoutBrackets
    .replace(/\+?\d[\d\s-]{5,}\d/g, ' ')
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean);

  const primaryDigits = digitRuns[0] || '';
  const primaryNormalized = primaryDigits.replace(/\D/g, '');

  let primary = primaryDigits || words.join(' ') || raw;
  // Prefer the clearer local presentation if both exist and match
  if (primaryDigits) primary = primaryDigits;

  let secondary: string | undefined;
  if (bracket?.[1]) {
    const alt = bracket[1].trim();
    const altNorm = alt.replace(/\D/g, '');
    if (altNorm && altNorm !== primaryNormalized && !altNorm.endsWith(primaryNormalized) && !primaryNormalized.endsWith(altNorm)) {
      secondary = alt;
    } else if (words.length && !primaryDigits.includes(words[0])) {
      // e.g. carrier name "Avoxi" when numbers are essentially the same
      secondary = words.join(' ');
    }
  } else if (digitRuns.length > 1) {
    const second = digitRuns[1];
    const secondNorm = second.replace(/\D/g, '');
    if (secondNorm !== primaryNormalized) secondary = second;
  } else if (words.length && primaryDigits) {
    secondary = words.join(' ');
  }

  // Cap visual length — keep roughly 10–12 visible chars on first line
  if (primary.length > 14 && primaryNormalized.length >= 9) {
    primary = primaryDigits || primary.slice(0, 12);
  }

  return { primary, secondary };
}

export function QueueMissedCallReport() {
  const { session } = useModuleAccess('reporting');
  const defaults = defaultDateRange();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [datePreset, setDatePreset] = useState<DatePreset>('week');
  const [queues, setQueues] = useState<Queue[]>([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<'missed' | 'all'>('missed');
  const [callFrom, setCallFrom] = useState('');
  const [abandonTime, setAbandonTime] = useState('5');
  const [loadingQueues, setLoadingQueues] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [rows, setRows] = useState<QueueCallDetail[]>([]);
  const [queueConfigById, setQueueConfigById] = useState<Map<string, QueueDetail>>(
    () => new Map()
  );

  useEffect(() => {
    (async () => {
      try {
        setLoadingQueues(true);
        const list = await fetchQueues();
        setQueues(list);
        setSelectedQueueIds(list.map((q) => q.id));
      } catch (error: any) {
        toast.error('Failed to load queues');
        console.error(error);
      } finally {
        setLoadingQueues(false);
      }
    })();
  }, []);

  const resolveConfig = useMemo(() => queueConfigResolver(queueConfigById), [queueConfigById]);

  const applyPreset = (preset: 'today' | 'week' | 'month') => {
    const range = getPresetHtmlDateRange(preset);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setDatePreset(preset);
  };

  const filteredRows = useMemo(() => filterQueueRows(rows, statusFilter, callFrom), [rows, statusFilter, callFrom]);

  const summary = useMemo(
    () => summarizeQueueRows(rows, statusFilter, filteredRows.length),
    [rows, statusFilter, filteredRows.length]
  );

  /** "All queues" when every queue is chosen, so queues added later are included too. */
  const scheduleFilters = {
    queueIds: selectedQueueIds.length === queues.length ? [] : selectedQueueIds,
    statusFilter,
    callFrom: callFrom.trim() || undefined,
    abandonTime: abandonTime ? Number(abandonTime) : undefined,
  };

  const exportPdf = async () => {
    try {
      const names = new Map(queues.map((q) => [q.id, q.name || q.number || q.id]));
      queueMissedPdf({
        rows: filteredRows,
        allRows: rows,
        statusFilter,
        filtersText: describeFilters('queueMissed', scheduleFilters, { queues: names }),
        resolveConfig,
        startDate,
        endDate,
        customerName: session?.customer?.name,
        assets: await loadPdfAssets(),
      }).save(`queue-missed-calls-${startDate}-to-${endDate}.pdf`);
    } catch (error) {
      console.error(error);
      toast.error('Could not make the PDF');
    }
  };

  const toggleQueue = (id: string) => {
    setSelectedQueueIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllQueues = () => setSelectedQueueIds(queues.map((q) => q.id));
  const clearQueues = () => setSelectedQueueIds([]);

  const generate = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date');
      return;
    }
    if (selectedQueueIds.length === 0) {
      toast.error('Select at least one queue');
      return;
    }

    setLoading(true);
    try {
      const { rows: enriched, configById } = await loadQueueMissed({
        startDate,
        endDate,
        queueIds: selectedQueueIds,
        abandonTime: abandonTime ? Number(abandonTime) : undefined,
      });
      setQueueConfigById(configById);
      setRows(enriched);
      setHasGenerated(true);
      const missCount = enriched.filter(isMissOrAbandon).length;
      if (enriched.length === 0) {
        toast('No queue call activity for this period', { icon: '📞' });
      } else {
        toast.success(
          `${enriched.length} queue call${enriched.length === 1 ? '' : 's'} · ${missCount} missed/abandoned`
        );
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || 'Failed to load queue missed call report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
            <Headphones className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Queue Missed Calls
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              After IVR → queue: see why a call was not handled — caller hung up, or agents
              were rung and nobody answered — and who was offered the call.
            </p>
          </div>
        </div>

        {/* Date presets */}
        <div className="flex flex-wrap gap-2 mb-4">
          {(
            [
              { id: 'today' as const, label: 'Today' },
              { id: 'week' as const, label: 'This week' },
              { id: 'month' as const, label: 'This month' },
            ] as const
          ).map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                datePreset === p.id
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
          <span
            className={`px-3 py-1.5 rounded-lg text-sm ${
              datePreset === 'custom'
                ? 'bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            or pick dates
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Start date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setDatePreset('custom');
                }}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              End date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setDatePreset('custom');
                }}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Show
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'missed' | 'all')}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 dark:[color-scheme:dark]"
            >
              <option value="missed">Missed & abandoned only</option>
              <option value="all">All queue calls (incl. answered)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Ignore short abandons (sec)
            </label>
            <Input
              type="number"
              min={0}
              value={abandonTime}
              onChange={(e) => setAbandonTime(e.target.value)}
              placeholder="e.g. 5"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Hang-ups during the greeting (under this many seconds) are hidden.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Call from (filter table)
          </label>
          <Input
            value={callFrom}
            onChange={(e) => setCallFrom(e.target.value)}
            placeholder="Search caller number from email / notification"
          />
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              Queues
            </label>
            <div className="flex gap-2 text-xs">
              <button
                type="button"
                onClick={selectAllQueues}
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearQueues}
                className="text-gray-500 hover:underline"
              >
                Clear
              </button>
            </div>
          </div>
          {loadingQueues ? (
            <p className="text-sm text-gray-500">Loading queues…</p>
          ) : queues.length === 0 ? (
            <p className="text-sm text-gray-500">No queues found on this PBX.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {queues.map((q) => {
                const active = selectedQueueIds.includes(q.id);
                return (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => toggleQueue(q.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-red-300'
                    }`}
                  >
                    {q.name}
                    {q.number ? ` (${q.number})` : ''}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={generate}
            disabled={loading || loadingQueues}
            className="inline-flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading…
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                Generate report
              </>
            )}
          </Button>
          <Button
            variant="secondary"
            disabled={!hasGenerated || filteredRows.length === 0}
            onClick={() =>
              downloadText(
                queueMissedCsv(filteredRows, resolveConfig),
                `queue-missed-calls-${startDate}-to-${endDate}.csv`,
                'text/csv;charset=utf-8'
              )
            }
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="secondary" disabled={!hasGenerated || filteredRows.length === 0} onClick={exportPdf}>
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <ScheduleReportButton
            kind="queueMissed"
            className="ml-auto self-center"
            filters={scheduleFilters}
            dateRange={datePreset === 'today' ? 'today' : datePreset === 'week' ? 'thisWeek' : datePreset === 'month' ? 'thisMonth' : undefined}
          />
        </div>
      </Card>

      {loading && (
        <Card>
          <Loader text="Loading queue call details from PBX…" />
        </Card>
      )}

      {!loading && hasGenerated && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="!p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Shown</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {summary.shown}
              </p>
            </Card>
            <Card className="!p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">No one answered</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                {summary.missed}
              </p>
            </Card>
            <Card className="!p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Caller hung up</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                {summary.abandoned}
              </p>
            </Card>
            {statusFilter === 'all' && (
              <Card className="!p-4">
                <p className="text-xs text-gray-500 dark:text-gray-400">Answered</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {summary.answered}
                </p>
              </Card>
            )}
          </div>

          <Card>
            <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-900/20 p-3 text-sm text-amber-950 dark:text-amber-100 space-y-1.5">
              <p className="font-semibold flex items-center gap-1.5">
                <PhoneMissed className="w-4 h-4" />
                How to read this report
              </p>
              <ul className="list-disc pl-5 space-y-1 text-amber-900/90 dark:text-amber-100/90">
                <li>
                  <strong>Caller hung up</strong> — they left while waiting; nobody answered
                  before they hung up.
                </li>
                <li>
                  <strong>No one answered</strong> — nobody took the call while it was in
                  queue. Wait may be shorter than the queue timeout if the ring cycle ended
                  earlier; <strong>Failover</strong> is where the PBX would send the call
                  after max wait.
                </li>
                <li>
                  <strong>Wait</strong> — how long this call spent in the queue. Compare to
                  the queue’s <strong>Timeout</strong> column when diagnosing max-wait cases.
                </li>
                <li>
                  <strong>Agents / Strategy / Timeout / Failover</strong> — current queue
                  configuration from the PBX (who can take calls, how they ring, how long, and
                  where the call goes next).
                </li>
              </ul>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="py-3 pr-3 font-semibold">Time</th>
                    <th className="py-3 pr-3 font-semibold">Queue</th>
                    <th className="py-3 pr-2 font-semibold w-[6.75rem] max-w-[6.75rem]">
                      Call from
                    </th>
                    <th className="py-3 pr-3 font-semibold">Outcome</th>
                    <th className="py-3 pr-3 font-semibold">Wait</th>
                    <th className="py-3 pr-3 font-semibold">Agents in queue</th>
                    <th className="py-3 pr-3 font-semibold">Strategy</th>
                    <th className="py-3 pr-3 font-semibold">Timeout</th>
                    <th className="py-3 pr-3 font-semibold">Failover</th>
                    <th className="py-3 font-semibold">What happened</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className="py-10 text-center text-gray-500 dark:text-gray-400"
                      >
                        No queue calls match the current filters.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => {
                      const st = normalizeStatus(row.status);
                      const cfg = resolveConfig(row);
                      const timeoutLabel = formatTimeout(cfg?.maxWaitTime);
                      const from = formatCallFrom(row.call_from);
                      return (
                        <tr
                          key={
                            row.id ||
                            `${row.time}-${row.call_from}-${row.queue_num}-${row.status}`
                          }
                          className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 align-top"
                        >
                          <td className="py-3 pr-3 whitespace-nowrap text-gray-900 dark:text-gray-100">
                            {row.time}
                          </td>
                          <td className="py-3 pr-3 text-gray-900 dark:text-gray-100">
                            <div className="font-medium">{row.queue_name || '—'}</div>
                            {row.queue_num && (
                              <div className="text-xs text-gray-500">{row.queue_num}</div>
                            )}
                          </td>
                          <td
                            className="py-3 pr-2 w-[6.75rem] max-w-[6.75rem] align-top"
                            title={row.call_from || undefined}
                          >
                            <div className="font-mono text-sm tabular-nums text-gray-900 dark:text-gray-100 leading-snug break-all">
                              {from.primary}
                              <ContactNameLine value={row.call_from} />
                              {from.secondary ? (
                                <div className="text-[11px] text-gray-500 dark:text-gray-400 font-sans">
                                  {from.secondary}
                                </div>
                              ) : null}
                            </div>
                          </td>
                          <td className="py-3 pr-3">
                            <Badge
                              variant={
                                st === 'answered'
                                  ? 'success'
                                  : st === 'abandoned'
                                    ? 'warning'
                                    : st === 'missed'
                                      ? 'danger'
                                      : 'default'
                              }
                            >
                              {outcomeLabel(row.status)}
                            </Badge>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 max-w-[12rem]">
                              {outcomeHelp(row.status, row.ring_duration, cfg?.maxWaitTime)}
                            </div>
                          </td>
                          <td className="py-3 pr-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                            <span className="font-medium">
                              {formatDuration(row.ring_duration)}
                            </span>
                          </td>
                          <td className="py-3 pr-3 text-gray-800 dark:text-gray-200 max-w-[14rem]">
                            {cfg?.agents?.length ? (
                              <ul className="list-disc pl-4 space-y-0.5">
                                {cfg.agents.map((a) => (
                                  <li key={a.number}>
                                    {a.name ? (
                                      <>
                                        <span className="font-medium">{a.number}</span>{' '}
                                        {a.name}
                                      </>
                                    ) : (
                                      a.number
                                    )}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-gray-400 text-xs">No agents configured</span>
                            )}
                          </td>
                          <td className="py-3 pr-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {cfg?.ringStrategy || '—'}
                          </td>
                          <td className="py-3 pr-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {timeoutLabel}
                          </td>
                          <td className="py-3 pr-3 text-gray-800 dark:text-gray-200 max-w-[12rem]">
                            {formatFailover(cfg)}
                          </td>
                          <td className="py-3 text-gray-800 dark:text-gray-200 max-w-md">
                            {row.what_happened ||
                              outcomeHelp(row.status, row.ring_duration, cfg?.maxWaitTime) ||
                              '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
